import type { AGENT_DATA_BASIS_KINDS } from '@tabel/shared/schemas/agent-data';
import { RESULT_TEXT } from '../Rules/texts';

type AgentDataBasisKind = (typeof AGENT_DATA_BASIS_KINDS)[number];

/*
 * Тексты страницы «Данные для ИИ-агентов» `/admin/agent-data` (005, T139; FR-026, FR-028, FR-060; spec.md, US7).
 * Язык — ASD-STE100: короткие предложения, условие первым, одно слово — одно значение, без латиницы. Проверяет
 * `texts.test.tsx` (`@tabel/shared/plain-language`). Названия клиентов, имена пользователей, номера документов и
 * условия выборок приходят с сервера — здесь их нет; названия правил — со страницы «Правила расчёта». Нейтральный
 * род: «Автор», а не «Изменил».
 */

/** Заголовки — до 4 слов. */
export const TITLE = {
  page: 'Данные для ИИ-агентов',
  tabs: 'Разделы страницы',
  permission: 'Разрешение на выборки',
  history: 'История изменений',
  log: 'Журнал выборок',
  state: 'Выборки дней',
  basis: 'Основание',
  clients: 'ИИ-клиенты',
  limits: 'Лимиты',
  loadError: 'Разрешение не загрузилось',
  logError: 'Журнал не загрузился',
  forbidden: 'Страница недоступна',
  disableConfirm: 'Выключить выборки дней?',
  historyEmpty: 'Изменений нет',
  logEmpty: 'Выборок нет',
  /* 006: компьютеры всех пользователей (T116) */
  computers: 'Компьютеры пользователей',
  computersEmpty: 'Компьютеров нет',
  computersError: 'Компьютеры не загрузились',
} as const;

/**
 * Вкладки страницы — коротко: на телефоне (375 px) первые три видны без прокрутки ленты, «Компьютеры» (006) — за
 * прокруткой ленты вбок (дизайн-система, Tabs).
 */
export const TAB = {
  permission: 'Разрешение',
  history: 'История',
  log: 'Журнал выборок',
  computers: 'Компьютеры',
} as const;

/** Подписи полей, кнопок и состояний — до 6 слов. */
export const LABEL = {
  on: 'Включены',
  off: 'Выключены',
  stateOn: 'Выборки включены',
  stateOff: 'Выборки выключены',
  basisKind: 'Вид основания',
  basisRef: 'Номер документа',
  basisDate: 'Дата документа',
  perResponse: 'Дней в одном ответе',
  perDay: 'Дней в сутки на пользователя',
  save: 'Сохранить изменения',
  saving: 'Сохранение…',
  reset: 'Отменить изменения',
  cancel: 'Отмена',
  disable: 'Выключить выборки',
  retry: 'Повторить',
  loading: 'Загрузка…',
  errorCode: 'Код ошибки:',
  author: 'Автор',
  when: 'Когда',
  who: 'Кто',
  client: 'Клиент',
  condition: 'Условие',
  days: 'Дней',
  basisShort: 'Основание',
  clientsShort: 'Клиенты',
  limitsShort: 'Лимиты',
  noBasis: 'Не указано',
  noClients: 'Нет',
  fromUi: 'Интерфейс табеля',
  smallGroup: 'Меньше 5 человек',
  from: 'С',
  to: 'По',
  person: 'Номер СКУД человека',
  show: 'Показать',
  allSamples: 'Все выборки',
  changes: 'Изменения',
  samples: 'Выборки',
  personDays: 'Переданные дни',
  sampleNo: 'Выборка',
  /* 006: компьютеры всех пользователей (T116) */
  owner: 'Владелец',
  system: 'Система',
  connector: 'Коннектор',
  computersLoading: 'Загрузка компьютеров…',
} as const;

/** Вид основания разрешения (`AGENT_DATA_BASIS_KINDS`, FR-028): подпись варианта. */
export const BASIS_KIND_LABEL = {
  договор_с_обработчиком: 'Договор с обработчиком',
  согласие_работников: 'Согласие работников',
} as const satisfies Record<AgentDataBasisKind, string>;

/** Предложения страницы. */
export const T = {
  intro:
    'Здесь вы включаете и выключаете выборки дней для ИИ-агентов на этом объекте. Агент получает дни под меткой, без ФИО и табельных номеров.',
  stateHint: 'Пока выборки выключены, агенты не получают дни. Включить выборки можно только с основанием.',
  basisHint: 'Укажите договор с обработчиком или согласие работников: номер и дату документа.',
  clientsHint: 'Выборки получают только выбранные клиенты.',
  limitsHint: 'Лимиты задают, сколько дней агент получает в одном ответе и за сутки.',
  noKnownClients: 'Сервер не прислал список клиентов.',
  basisRequired: 'Чтобы включить выборки, укажите основание.',
  errKind: 'Выберите вид основания.',
  errRef: 'Укажите номер документа.',
  errDate: 'Укажите дату документа.',
  errPerResponse: 'Введите целое число от 1 до 20.',
  errPerDay: 'Введите целое число от 1 до 10 000.',
  saveFailed: 'Разрешение не сохранено',
  saved: 'Разрешение сохранено',
  enabled: 'Выборки включены.',
  disabled: 'Выборки выключены. Ссылки выборок больше не работают.',
  disableText:
    'Агенты больше не получат дни объекта. Все выданные ссылки выборок перестанут работать. Журнал выборок останется.',
  forbidden: 'Разрешение на выборки меняет только администратор системы.',
  forbiddenFix: 'Если у вас есть роль «Администратор системы», выберите её.',
  historyIntro:
    'История показывает каждое включение, выключение и изменение разрешения. Новые записи стоят сверху.',
  historyEmpty: 'Разрешение ещё не меняли.',
  logIntro:
    'Журнал показывает, кто и каким клиентом получил дни. Поиск по номеру показывает, какие дни человека ушли агентам.',
  logEmpty: 'За этот период выборок нет.',
  personEmpty: 'За этот период дни этого человека агентам не передавали.',
  periodError: 'Дата «С» не может быть позже даты «По».',
  personHint: 'Введите номер человека в СКУД, как в табеле.',
  truncated: 'Показаны последние 500 выборок. Чтобы увидеть остальные, сузьте период.',
  /* 006: компьютеры всех пользователей (T116; spec.md, «Права ролей») */
  computersIntro:
    'Здесь показаны компьютеры с коннектором у всех пользователей. Переписку с агентами здесь не видно.',
  computersEmpty: 'Здесь появятся компьютеры, которые пользователи подключат к табелю.',
} as const;

/**
 * Условие выборки словами: подписи частей (FR-023). Значения — с сервера; название правила — вопрос правила со
 * страницы «Правила расчёта», номер версии — из истории правил.
 */
export const CONDITION_LABEL = {
  group: 'Правило',
  ruleGone: 'Правило, которого сейчас нет в правилах',
  flag: 'Пометка',
  state: 'Состояние дня',
  errorCode: 'Ошибка расчёта',
  versionId: 'Версия правил',
  dates: 'Даты',
  hours: 'Часы',
  changedByDraft: 'Изменится черновиком',
  none: 'Без условия',
  dayLink: 'День по кнопке «Передать день агенту»',
} as const;

/** Состояние дня в условии (`day_results.state`) словами — как итог дня на странице «Правила расчёта». */
export const STATE_WORD: Readonly<Record<string, string>> = {
  ok: 'проходы в порядке',
  нет_выхода: RESULT_TEXT.noExit,
  нет_входа: RESULT_TEXT.noEntry,
  на_объекте: RESULT_TEXT.onSite,
  пусто: RESULT_TEXT.empty,
};

/** Число со словом по правилам русского языка: 1 день, 2 дня, 5 дней. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** Тексты с подстановкой. Даты — уже «дд.мм.гггг», имена и номера — с сервера. */
export const say = {
  days: (n: number) => `${n} ${plural(n, 'день', 'дня', 'дней')}`,
  sample: (id: number) => `Выборка № ${id}`,
  limits: (perResponse: number, perDay: number) => `${perResponse} в ответе, ${perDay} в сутки`,
  basis: (kind: string, ref: string, date: string) => `${kind} № ${ref} от ${date}`,
  dates: (from: string | null, to: string | null) =>
    from && to ? `с ${from} по ${to}` : from ? `с ${from}` : `по ${to ?? ''}`,
  hours: (min: number | null, max: number | null) =>
    min !== null && max !== null
      ? `от ${min} до ${max} ч`
      : min !== null
        ? `от ${min} ч`
        : `до ${max ?? 0} ч`,
  /** Номер версии правил — как на странице «Правила расчёта» («Версия 3»). */
  version: (n: number) => `${CONDITION_LABEL.versionId} ${n}`,
  /** Правило — его вопросом со страницы «Правила расчёта». */
  rule: (question: string) => `${CONDITION_LABEL.group} «${question}»`,
  /** Пометка расчёта словами (`flagText`). */
  flag: (text: string) => `${CONDITION_LABEL.flag} «${text}»`,
  /** Код ошибки расчёта, для которого нет слов: подпись и код отдельно. */
  errorCode: (code: string) => `${CONDITION_LABEL.errorCode}, код ${code}`,
  /** Человек в журнале — номером СКУД: ФИО в ответе сервера нет. */
  person: (personId: string) => `№ СКУД ${personId}`,
  /** Последнее изменение разрешения: когда и автор. */
  changed: (when: string, who: string | null) => (who ? `Изменено ${when} · ${who}` : `Изменено ${when}`),
  /** Окно отключения чужого компьютера: чей он (вход владельца в табель). */
  computerOwner: (owner: string) => `Компьютер принадлежит пользователю ${owner}.`,
};
